import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BoothBan {
  id: string;
  booth_id: string;
  user_id: string;
  kicked_by: string;
  kicked_at: string;
  reason: string | null;
  ban_duration_minutes: number | null;
}

export const useBoothBans = (boothId: string | undefined, currentUserId: string | undefined) => {
  const [bannedUsers, setBannedUsers] = useState<Set<string>>(new Set());
  const [isCurrentUserBanned, setIsCurrentUserBanned] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch existing bans for this booth
  const fetchBans = useCallback(async () => {
    if (!boothId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("exhibition_booth_kicks")
        .select("*")
        .eq("booth_id", boothId);

      if (error) throw error;

      // Filter active bans (check if ban duration hasn't expired)
      const activeBans = (data || []).filter((ban: BoothBan) => {
        if (!ban.ban_duration_minutes) return true; // Permanent ban
        const kickedAt = new Date(ban.kicked_at || ban.kicked_at);
        const expiry = new Date(kickedAt.getTime() + ban.ban_duration_minutes * 60 * 1000);
        return expiry > new Date();
      });

      const bannedUserIds = new Set(activeBans.map((b: BoothBan) => b.user_id));
      setBannedUsers(bannedUserIds);

      if (currentUserId) {
        setIsCurrentUserBanned(bannedUserIds.has(currentUserId));
      }
    } catch (error) {
      console.error("[BoothBans] Error fetching bans:", error);
    } finally {
      setLoading(false);
    }
  }, [boothId, currentUserId]);

  useEffect(() => {
    fetchBans();
  }, [fetchBans]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!boothId) return;

    const channel = supabase
      .channel(`booth-bans:${boothId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "exhibition_booth_kicks",
          filter: `booth_id=eq.${boothId}`,
        },
        () => {
          fetchBans();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [boothId, fetchBans]);

  // Ban a user (persist to database)
  const banUser = useCallback(async (
    targetUserId: string,
    adminId: string,
    reason?: string,
    durationMinutes?: number // null = permanent for this booth
  ) => {
    if (!boothId) return false;

    try {
      const { error } = await supabase
        .from("exhibition_booth_kicks")
        .insert({
          booth_id: boothId,
          user_id: targetUserId,
          kicked_by: adminId,
          reason: reason || "Banned by booth admin",
          ban_duration_minutes: durationMinutes || null, // null = permanent
        });

      if (error) throw error;

      setBannedUsers(prev => new Set(prev).add(targetUserId));
      toast.success("User has been banned from this booth");
      return true;
    } catch (error) {
      console.error("[BoothBans] Error banning user:", error);
      toast.error("Failed to ban user");
      return false;
    }
  }, [boothId]);

  // Check if a user is banned
  const isUserBanned = useCallback((userId: string) => {
    return bannedUsers.has(userId);
  }, [bannedUsers]);

  return {
    bannedUsers,
    isCurrentUserBanned,
    loading,
    banUser,
    isUserBanned,
    refetch: fetchBans,
  };
};
